/**
 * Zoom no sítio do clique: a captura é guardada inteira e o recorte é calculado ao mostrar
 * (no painel, no editor e nas exportações). Assim o autor pode desligar o zoom ou mudar o
 * ponto sem perder a imagem.
 */
import type { Annotation } from './annotate';
import type { Box, Point } from './steps';

export interface ZoomView {
  /** Área da captura a mostrar (fração 0…1). */
  crop: Box;
  /** Posição do marcador dentro do recorte (fração 0…1). */
  marker: Point;
}

const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));

/**
 * Recorte centrado no ponto (mantém a proporção da captura). factor = quanto aproxima
 * (2 = metade da largura). Com a caixa do elemento, aproxima menos se o elemento for alto
 * (um painel, um cartão), para se perceber o que é; um elemento largo e baixo (um campo,
 * uma linha) não impede o zoom: o recorte fica à volta do clique.
 */
export function zoomView(
  point: Point | undefined,
  box?: Box,
  factor = 2.2,
  minFactor = 1.5,
): ZoomView {
  if (!point) return { crop: { x: 0, y: 0, w: 1, h: 1 }, marker: { x: 0.5, y: 0.5 } };
  let z = Math.max(1, factor);
  if (box && box.h > 0) {
    // A altura do elemento deve ocupar no máximo ~50% do recorte
    z = clamp(Math.min(z, 0.5 / box.h), Math.min(minFactor, factor), factor);
  }
  const w = 1 / z;
  const h = 1 / z;
  const x = clamp(point.x - w / 2, 0, 1 - w);
  const y = clamp(point.y - h / 2, 0, 1 - h);
  return { crop: { x, y, w, h }, marker: { x: (point.x - x) / w, y: (point.y - y) / h } };
}

/** Caixa que contém todas as anotações (fração da captura). */
function annotationsBounds(list: Annotation[]): Box | null {
  if (!list.length) return null;
  let x1 = 1;
  let y1 = 1;
  let x2 = 0;
  let y2 = 0;
  for (const a of list) {
    const pts = a.kind === 'arrow' ? [a.from, a.to] : [a, { x: a.x + a.w, y: a.y + a.h }];
    for (const p of pts) {
      x1 = Math.min(x1, p.x);
      y1 = Math.min(y1, p.y);
      x2 = Math.max(x2, p.x);
      y2 = Math.max(y2, p.y);
    }
  }
  return { x: clamp(x1, 0, 1), y: clamp(y1, 0, 1), w: clamp(x2, 0, 1) - clamp(x1, 0, 1), h: clamp(y2, 0, 1) - clamp(y1, 0, 1) };
}

/**
 * Alarga o zoom automático (quadrado, em fração) para caberem as anotações do autor,
 * com uma pequena margem; mantém a proporção da captura.
 */
function zoomWithAnnotations(view: ZoomView, point: Point, list: Annotation[]): ZoomView {
  const b = annotationsBounds(list);
  if (!b) return view;
  const c = view.crop;
  const pad = 0.03;
  const x1 = Math.min(c.x, b.x - pad);
  const y1 = Math.min(c.y, b.y - pad);
  const x2 = Math.max(c.x + c.w, b.x + b.w + pad);
  const y2 = Math.max(c.y + c.h, b.y + b.h + pad);
  const side = Math.min(1, Math.max(x2 - x1, y2 - y1));
  if (side <= c.w + 1e-9 && x1 >= c.x && y1 >= c.y) return view;
  const x = clamp((x1 + x2) / 2 - side / 2, 0, 1 - side);
  const y = clamp((y1 + y2) / 2 - side / 2, 0, 1 - side);
  return { crop: { x, y, w: side, h: side }, marker: { x: (point.x - x) / side, y: (point.y - y) / side } };
}

/** O zoom no clique pode ligar-se e desligar-se: há ponto de clique e nenhum recorte do autor
 *  (o recorte ganha ao zoom). */
export const canZoom = (step: { point?: Point; crop?: Box }): boolean => !!step.point && !step.crop;

/** Zoom de todas as capturas do guia: ligado em todas, desligado em todas, ou misto (null: não
 *  há nenhuma onde se possa escolher). */
export function zoomAll(steps: { point?: Point; crop?: Box; zoom?: boolean }[]): 'on' | 'off' | 'mixed' | null {
  const list = steps.filter(canZoom);
  if (!list.length) return null;
  const on = list.filter((s) => s.zoom !== false).length;
  return on === list.length ? 'on' : on === 0 ? 'off' : 'mixed';
}

/** Liga ou desliga o zoom em todas as capturas onde se pode escolher (as recortadas ficam). */
export function setZoomAll<T extends { point?: Point; crop?: Box; zoom?: boolean }>(steps: T[], on: boolean): T[] {
  return steps.map((s) => (canZoom(s) ? { ...s, zoom: on } : s));
}

/**
 * O que mostrar de uma captura: o recorte do autor; senão o zoom no clique (se ligado,
 * alargado para mostrar as anotações); senão a imagem inteira. O marcador só aparece se o
 * ponto estiver dentro da área.
 */
export function stepView(step: {
  point?: Point;
  box?: Box;
  crop?: Box;
  zoom?: boolean;
  annotations?: Annotation[];
}): ZoomView & { showMarker: boolean } {
  if (step.crop && step.crop.w > 0 && step.crop.h > 0) {
    const c = step.crop;
    const p = step.point;
    const inside = !!p && p.x >= c.x && p.x <= c.x + c.w && p.y >= c.y && p.y <= c.y + c.h;
    return {
      crop: c,
      marker: p ? { x: (p.x - c.x) / c.w, y: (p.y - c.y) / c.h } : { x: 0.5, y: 0.5 },
      showMarker: inside,
    };
  }
  if (step.zoom !== false && step.point)
    return {
      ...zoomWithAnnotations(zoomView(step.point, step.box), step.point, step.annotations ?? []),
      showMarker: true,
    };
  return {
    crop: { x: 0, y: 0, w: 1, h: 1 },
    marker: step.point || { x: 0.5, y: 0.5 },
    showMarker: !!step.point,
  };
}

/** Normaliza um rectângulo desenhado com o rato (dois cantos, em fração) e limita-o à imagem. */
export function rectFrom(a: Point, b: Point): Box {
  const x1 = clamp(Math.min(a.x, b.x), 0, 1);
  const y1 = clamp(Math.min(a.y, b.y), 0, 1);
  const x2 = clamp(Math.max(a.x, b.x), 0, 1);
  const y2 = clamp(Math.max(a.y, b.y), 0, 1);
  return { x: x1, y: y1, w: x2 - x1, h: y2 - y1 };
}

/** Estilo CSS para mostrar o recorte com uma <img> dentro de um contentor overflow:hidden. */
export function cropStyle(crop: Box): { width: string; left: string; top: string } {
  return {
    width: (100 / crop.w).toFixed(3) + '%',
    left: ((-crop.x / crop.w) * 100).toFixed(3) + '%',
    top: ((-crop.y / crop.h) * 100).toFixed(3) + '%',
  };
}
